import { useCallback, useMemo, useSyncExternalStore } from 'react';

import dayjs from 'dayjs';
import enGB from 'dayjs/locale/en-gb';

import { getLanguage, selectLanguage } from '@cognite/cdf-i18n-utils';
import type { Language, LanguageCode } from '@cognite/user-profile-components';

import { DEFAULT_LANGUAGE, languageLabels } from './constants';
import { i18n } from './i18n';
import { getIsUsingUSFormat } from './utils';

const isLanguageCode = (code: string): code is LanguageCode =>
  code in languageLabels;

const getSupportedLanguages = (): Language[] => {
  return Object.keys(languageLabels)
    .filter(isLanguageCode)
    .map((code: Language['code']) => ({
      code,
      label: languageLabels[code] ?? '',
    }))
    .filter((language: Language) => Boolean(language.label))
    .sort((a: Language, b: Language) => (a.label >= b.label ? 1 : -1));
};

const SUPPORTED_LANGUAGES = getSupportedLanguages();

export const resolveSelectedLanguage = (
  languageCode: string,
  supportedLanguages: Language[] = SUPPORTED_LANGUAGES
): Language =>
  supportedLanguages.find((language) => language.code === languageCode) ||
  supportedLanguages.find(
    (language) => language.code === DEFAULT_LANGUAGE.code
  ) ||
  supportedLanguages[0];

const applyDayjsLocaleForLanguageCode = (languageCode: string) => {
  if (languageCode === 'en') {
    dayjs.locale(getIsUsingUSFormat() ? 'en' : enGB);
  } else {
    dayjs.locale(languageCode);
  }

  return dayjs.locale();
};

let currentLanguageSnapshot = i18n.language ?? 'en';
const languageChangeListeners = new Set<() => void>();

// Shared store so every useInfieldLanguage() caller re-renders when the language
// changes. dayjs.locale() is global state React cannot observe on its own.
const subscribeToLanguageChange = (listener: () => void) => {
  languageChangeListeners.add(listener);
  return () => {
    languageChangeListeners.delete(listener);
  };
};

const getLanguageSnapshot = () => currentLanguageSnapshot;

// Keep dayjs in sync with i18n, notify hook subscribers
i18n.on('languageChanged', (languageCode: string) => {
  applyDayjsLocaleForLanguageCode(languageCode);
  currentLanguageSnapshot = languageCode;
  languageChangeListeners.forEach((listener) => listener());
});

export const useInfieldLanguage = () => {
  const language = useSyncExternalStore(
    subscribeToLanguageChange,
    getLanguageSnapshot
  );

  const updateLanguage = useCallback((lang?: Language) => {
    if (lang?.code) {
      i18n.changeLanguage(lang.code);
      selectLanguage(lang.code, false);
    }
  }, []);

  const getInitialLanguage = useCallback(() => {
    const rawStored = getLanguage();

    const selectedLanguage: LanguageCode | null =
      rawStored !== undefined && isLanguageCode(rawStored) ? rawStored : null;
    if (selectedLanguage) {
      updateLanguage({ code: selectedLanguage, label: '' });
    } else {
      updateLanguage(DEFAULT_LANGUAGE);
    }
  }, [updateLanguage]);

  const selectedLanguage = useMemo(
    () => resolveSelectedLanguage(language),
    [language]
  );

  return {
    updateLanguage,
    getInitialLanguage,
    selectedLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
  };
};
